import { connect } from 'react-redux';

// component functions
import * as OwnFunctions from './ContractAccess.List.Functions';
import { useEffect, useState } from 'react';
import { AvatarGroup, AvatarGroupItem, Button, Label, Persona, Popover, PopoverProps, PopoverSurface, PopoverTrigger, Spinner } from '@fluentui/react-components';
import ContractAccessFormComponent from '../ContractAccess.Form/ContractAccess.Form.Component';
import { ContractAccess } from '../../../../models/Entities/Contracts/ContractAccess';
import { Separator, Toggle } from '@fluentui/react';
import { Delete24Regular, Edit24Regular } from '@fluentui/react-icons';

//styles
import './ContractAccess.List.Styles.css';

// Returns badge presence based on read/write combination
const getAccessStatus = (access: ContractAccess): 'available' | 'away' | 'blocked' => {
    if (access.writeAccess) return 'available';   // green: full access
    if (access.readAccess) return 'away';          // yellow: read only
    return 'blocked';                              // red: no access
};

const getAccessLabel = (access: ContractAccess): string => {
    if (access.writeAccess) return 'Lectura y escritura';
    if (access.readAccess) return 'Solo lectura';
    return 'Sin acceso';
};

// --- Defined outside the parent to keep a stable component reference ---
interface UserAccessContentProps extends ContractAccess {
    isUpdating: boolean;
    onUpdate: (access: ContractAccess) => void;
    onDelete: (access: ContractAccess) => void;
    onEdit: (access: ContractAccess) => void;
}

const UserAccessContent = (props: UserAccessContentProps) => {
    const { isUpdating, onUpdate, onDelete, onEdit, ...access } = props;

    return (
        <div className='accessPopoverContent'>
            <Persona
                presence={{ status: getAccessStatus(access) }}
                size="large"
                name={access.userName}
                avatar={{ color: "colorful" }}
                secondaryText={getAccessLabel(access)}
            />
            <Separator />

            <div className='inlineToggle'>
                <Toggle
                    label="Puede ver el contrato (lectura)"
                    checked={access.readAccess}
                    onText="Sí"
                    offText="No"
                    disabled={isUpdating || access.writeAccess}
                    onChange={(ev: any, checked?: boolean) =>
                        onUpdate({
                            ...access,
                            readAccess: checked ?? false,
                            writeAccess: checked ? access.writeAccess : false
                        })
                    }
                />
                {isUpdating && <><Spinner size="extra-tiny" />&nbsp;</>}
            </div>

            <div className='inlineToggle'>
                <Toggle
                    label="Puede editar el contrato (escritura)"
                    checked={access.writeAccess}
                    onText="Sí"
                    offText="No"
                    disabled={isUpdating}
                    onChange={(ev: any, checked?: boolean) =>
                        onUpdate({
                            ...access,
                            writeAccess: checked ?? false,
                            readAccess: checked ? true : access.readAccess
                        })
                    }
                />
                {isUpdating && <><Spinner size="extra-tiny" />&nbsp;</>}
            </div>

            <Separator />

            <div className='accessPopoverActions'>
                <Button
                    icon={<Edit24Regular />}
                    onClick={() => onEdit(access)}
                >
                    Editar
                </Button>
                <Button
                    id='RemoveAccessButton'
                    icon={<Delete24Regular />}
                    onClick={() => onDelete(access)}
                >
                    Eliminar acceso
                </Button>
            </div>
        </div>
    );
};
// -----------------------------------------------------------------------

const ContractAccessListComponent = (props: OwnFunctions.Props) => {

    const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);

    // add or update form control
    const [isAccessFormOpen, setIsAccessFormOpen] = useState(false);
    const [selectedAccess, setSelectedAccess] = useState<ContractAccess | null>(null);

    const isUpdating = props.Contract?.isUpdatingContractAccess ?? false;

    // events
    const handleUpdate = (access: ContractAccess) => {
        props.UpdateContractAccess(access);
    };

    const handleDelete = (access: ContractAccess) => {
        props.DeleteContractAccess(props.ContractId, access.id);
        setOpenPopoverId(null);
    };

    const handleEdit = (access: ContractAccess) => {
        setSelectedAccess(access);
        setOpenPopoverId(null);
        setIsAccessFormOpen(true);
    };

    const onAccessFormDismiss = () => {
        setSelectedAccess(null);
        setIsAccessFormOpen(false);
    };

    useEffect(() => {
        props.GetContractAccessByContractId(props.ContractId);
        props.GetAllUsers(1, 1000, undefined);
    }, []);

    const Forms = (
        <ContractAccessFormComponent
            isOpen={isAccessFormOpen}
            openPanel={() => undefined}
            dismissPanel={onAccessFormDismiss}
            ContractAccessId={selectedAccess?.id}
            ContractId={props.ContractId}
            SelectedUsersIds={props.Contract?.access
                ?.filter(a => a.id !== selectedAccess?.id)
                .map(a => a.userID)}
        />
    );

    return (
        <>
            <label>Permisos</label>
            <p>
                <Button onClick={() => { setSelectedAccess(null); setIsAccessFormOpen(true); }}>
                    Habilitar usuarios
                </Button>
            </p>
            {props.Access?.length === 0 && (
                <p><Label>Este contrato es visible para cualquier usuario de administración</Label></p>
            )}
            {(props.Access || []).length > 0 && (
                <AvatarGroup layout="stack">
                    {props.Access?.map((access: ContractAccess) => {
                        const handleOpenChange: PopoverProps["onOpenChange"] = (e, data) =>
                            setOpenPopoverId(data.open ? access.userID : null);

                        return (
                            <Popover
                                withArrow={true}
                                key={access.userID}
                                open={openPopoverId === access.userID}
                                onOpenChange={handleOpenChange}
                            >
                                <PopoverTrigger disableButtonEnhancement>
                                    <AvatarGroupItem
                                        style={{ cursor: 'pointer' }}
                                        name={access.userName}
                                        key={access.userID}
                                        badge={{ status: getAccessStatus(access) }}
                                    />
                                </PopoverTrigger>
                                <PopoverSurface tabIndex={-1}>
                                    <UserAccessContent
                                        {...access}
                                        isUpdating={isUpdating}
                                        onUpdate={handleUpdate}
                                        onDelete={handleDelete}
                                        onEdit={handleEdit}
                                    />
                                </PopoverSurface>
                            </Popover>
                        );
                    })}
                </AvatarGroup>
            )}
            {Forms}
        </>
    );
};

export default connect(
    OwnFunctions.mapStateToProps,
    OwnFunctions.mapDispatchToProps
)(ContractAccessListComponent as any);